import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  FormGroup,
} from "reactstrap";
import { saveCashLedger } from "../../components/Api/GmsCashLedger";
import { getBanks } from "../../components/Api/GmsBank";
import { getBankAccounts } from "../../components/Api/GmsBankAccount";
import { getCostHeads } from "../../components/Api/GmsCostHead";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getSuppliers } from "../../components/Api/GmsSupplier";
import { getOtherPayees } from "../../components/Api/GmsOtherPayee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { checkDayClosing } from "../../components/Api/GmsDayClosing";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Helper to normalize ID values
  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  // Prioritize authUser (latest login response)
  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
  };
};

const defaultForm = {
  Date: new Date(),
  Amount: 0,
  TransactorType: null,
  TransactorId: null,
  CostHeadId: null,
  Notes: "",
};

export default function PettyCashPage() {
  const location = useLocation();
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Determine mode from URL path
  const isReceipt = location.pathname.includes('Receipts') && !location.pathname.includes('List');
  const pageTitle = isReceipt ? "Petty Cash Receipts" : "Petty Cash Payments";
  const breadcrumbTitle = isReceipt ? "Petty Cash Receipts" : "Petty Cash Payments";

  // Transactor type options (Cost Head removed - it's now a separate field)
  const transactorTypeOptions = [
    { value: 'ba', label: 'Banks' },
    { value: 'em', label: 'Employee' },
    { value: 'su', label: 'Supplier' },
    { value: 'ot', label: 'Others' },
  ];

  // State for transactor data
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [costHeads, setCostHeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [otherPayees, setOtherPayees] = useState([]);
  const [transactorOptions, setTransactorOptions] = useState([]);
  const [loadingTransactors, setLoadingTransactors] = useState(false);

  // Business and Branch state
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const pagePath = isReceipt ? '/GmsPettyCashReceipts' : '/GmsPettyCashPayments';
  const hasPermission = hasPagePermissionWithAdmin(permissions, pagePath);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Check day closing for selected date (triggered on date selection)
  const checkSelectedDateDayClosing = async (selectedDate) => {
    try {
      if (!selectedDate) return;

      const businessId = selectedBusiness?.value || null;
      const branchId = selectedBranch?.value || null;

      if (!businessId) {
        // Business not selected yet, skip validation
        return;
      }

      // Format selected date
      const selectedDateOnly = new Date(selectedDate);
      selectedDateOnly.setHours(0, 0, 0, 0);

      // Check selected date with previous days check enabled (stricter validation)
      const checkRequest = {
        transactionDate: formatDateForAPI(selectedDateOnly, false),
        businessId: businessId,
        branchId: branchId,
        checkPreviousDays: true  // Enable stricter validation
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);

      // Check if selected date itself is closed
      if (dayClosingCheck.isClosed) {
        const message = dayClosingCheck.isHoliday
          ? `Holiday has been marked for ${formatDateForDisplay(selectedDateOnly, 'date')}. Please select a different date.`
          : `Day closing has been marked for ${formatDateForDisplay(selectedDateOnly, 'date')}. Please select a different date.`;

        Swal.fire({
          icon: 'warning',
          title: 'Day Closed',
          text: message,
          confirmButtonText: 'OK'
        });

        setIsReadOnly(true);
        return;
      }

      // Check if any previous days are not closed (stricter validation)
      if (dayClosingCheck.firstUnclosedDate) {
        const unclosedDateStr = formatDateForDisplay(new Date(dayClosingCheck.firstUnclosedDate), 'date');
        const selectedDateStr = formatDateForDisplay(selectedDateOnly, 'date');

        Swal.fire({
          icon: 'warning',
          title: 'Previous Day Not Closed',
          text: `Day closing is not done for ${unclosedDateStr}. Please close that day first before creating transactions for ${selectedDateStr}.`,
          confirmButtonText: 'OK'
        });

        setIsReadOnly(true);
        return;
      }

      // All checks passed - enable the form
      setIsReadOnly(false);

    } catch (error) {
      console.error("Error checking day closing for selected date:", error);
      // Don't block on error - allow user to continue
    }
  };

  // Check day closing when date is selected
  useEffect(() => {
    if (form.Date && selectedBusiness?.value) {
      const timer = setTimeout(() => {
        checkSelectedDateDayClosing(form.Date);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [form.Date, selectedBusiness?.value, selectedBranch?.value]);
  const isViewOnly = hasViewOnlyPermission(permissions, pagePath);
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, pagePath);

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  useEffect(() => {
    if (form.TransactorType) {
      loadTransactors(form.TransactorType);
    } else {
      setTransactorOptions([]);
      setForm(prev => ({ ...prev, TransactorId: null }));
    }
  }, [form.TransactorType]);

  useEffect(() => {
    if (form.TransactorType === 'ba' && selectedBank) {
      loadBankAccounts(selectedBank.value);
    } else if (form.TransactorType === 'ba') {
      setBankAccounts([]);
    }
  }, [selectedBank, form.TransactorType]);

  useEffect(() => {
    console.log('[PettyCash] useEffect - selectedBusiness changed:', selectedBusiness);
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
    // Reload employees when business changes
    loadEmployees();
  }, [selectedBusiness]);

  // Reload employees when branch changes
  useEffect(() => {
    console.log('[PettyCash] useEffect - selectedBranch changed:', selectedBranch);
    if (selectedBranch || session.branchId) {
      loadEmployees();
    }
  }, [selectedBranch]);

  // Auto-select business/branch from session on mount
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !selectedBusiness) {
      const userBusiness = businesses.find(b => b.business_Id === session.businessId);
      if (userBusiness) {
        setSelectedBusiness({
          value: session.businessId,
          label: userBusiness.business_NameEn || userBusiness.business_Name || ''
        });
      }
    }
  }, [session.businessId, businesses, selectedBusiness]);

  useEffect(() => {
    if (session.branchId && branches.length > 0 && !selectedBranch) {
      const userBranch = branches.find(b => b.branch_Id === session.branchId);
      if (userBranch) {
        setSelectedBranch({
          value: session.branchId,
          label: `${userBranch.branch_NameEn || userBranch.branch_Name || ''} - ${userBranch.branch_NameAr || ''}`.trim()
        });
      }
    }
  }, [session.branchId, branches, selectedBranch]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Load all transactor types data and businesses
      const [banksResponse, costHeadsData, suppliersData, otherPayeesData, businessesData] = await Promise.all([
        getBanks({}),
        getCostHeads({}),
        getSuppliers({}),
        getOtherPayees({}),
        getBusinesses({ business_Active: true }),
      ]);

      // Extract banks array from response object (response has {banks: [...], totalCount, page, pageSize})
      const banksArray = Array.isArray(banksResponse?.banks) ? banksResponse.banks : (Array.isArray(banksResponse) ? banksResponse : []);
      setBanks(banksArray);

      // Other APIs return arrays directly, but add defensive checks
      setCostHeads(Array.isArray(costHeadsData) ? costHeadsData : []);
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      setOtherPayees(Array.isArray(otherPayeesData) ? otherPayeesData : []);

      // Handle businesses
      const businessesArray = Array.isArray(businessesData) ? businessesData : (businessesData?.data || []);
      setBusinesses(businessesArray);

      // Load employees will be handled separately with business/branch filter
      await loadEmployees();
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const filter = {};
      // Use selected business/branch or session business/branch
      const businessId = selectedBusiness?.value || session.businessId;
      const branchId = selectedBranch?.value || session.branchId;

      console.log('[PettyCash] loadEmployees - selectedBusiness:', selectedBusiness);
      console.log('[PettyCash] loadEmployees - selectedBranch:', selectedBranch);
      console.log('[PettyCash] loadEmployees - session.businessId:', session.businessId);
      console.log('[PettyCash] loadEmployees - session.branchId:', session.branchId);
      console.log('[PettyCash] loadEmployees - businessId:', businessId);
      console.log('[PettyCash] loadEmployees - branchId:', branchId);

      // Use PascalCase property names to match C# model (EmployeeFilter)
      if (businessId) {
        filter.Employee_Business_Id = businessId;
      }
      if (branchId && businessId) {
        filter.Employee_Branch_Id = branchId;
      }

      console.log('[PettyCash] loadEmployees - filter being sent to API:', filter);

      const employeesData = await getEmployees(filter);

      console.log('[PettyCash] loadEmployees - employeesData received:', employeesData?.length || 0, 'employees');
      console.log('[PettyCash] loadEmployees - sample employee data:', employeesData?.[0]);

      setEmployees(Array.isArray(employeesData) ? employeesData : []);

      // If employee transactor type is selected, reload transactor options
      if (form.TransactorType === 'em') {
        loadTransactors('em');
      }
    } catch (error) {
      console.error("Error loading employees:", error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const loadBranches = async (businessId) => {
    if (!businessId) {
      setBranches([]);
      return;
    }
    try {
      setLoadingBranches(true);
      const response = await getBranches({ Business_Id: businessId, branch_Active: true });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      setBranches(branchData);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    } finally {
      setLoadingBranches(false);
    }
  };

  const loadTransactors = async (transactorType) => {
    try {
      setLoadingTransactors(true);
      setTransactorOptions([]);
      setForm(prev => ({ ...prev, TransactorId: null }));

      console.log('[PettyCash] loadTransactors called with type:', transactorType);
      console.log('[PettyCash] loadTransactors - selectedBusiness:', selectedBusiness);
      console.log('[PettyCash] loadTransactors - selectedBranch:', selectedBranch);
      console.log('[PettyCash] loadTransactors - employees state:', employees?.length || 0, 'employees');

      switch (transactorType) {
        case 'ba':
          // For banks, show bank list first
          // Handle both array and ensure it's an array
          const banksArray = Array.isArray(banks) ? banks : [];
          const bankOptions = banksArray.map(b => ({
            value: b.bankId || b.BankId || b.bank_Id || b.Bank_Id,
            label: b.bankNameEn || b.BankNameEn || b.bank_NameEn || b.Bank_NameEn || b.bank_Name || b.Bank_Name || '',
          }));
          setTransactorOptions(bankOptions);
          break;
        case 'em':
          // Handle both array and ensure it's an array
          const employeesArray = Array.isArray(employees) ? employees : [];
          console.log('[PettyCash] loadTransactors - employeesArray length:', employeesArray.length);
          console.log('[PettyCash] loadTransactors - sample employee:', employeesArray[0]);
          console.log('[PettyCash] loadTransactors - checking employee business IDs:', employeesArray.slice(0, 5).map(e => ({
            id: e.employee_Id || e.Employee_Id,
            businessId: e.employee_Business_Id || e.Employee_Business_Id,
            name: e.employee_NameEn || e.Employee_NameEn
          })));

          const employeeOptions = employeesArray.map(emp => ({
            value: emp.employee_Id || emp.Employee_Id || emp.employeeId || emp.EmployeeId,
            label: `${emp.employee_Code || emp.Employee_Code || emp.employeeCode || emp.EmployeeCode || ''} - ${emp.employee_NameEn || emp.Employee_NameEn || emp.employeeNameEn || emp.EmployeeNameEn || ''}`.trim(),
          }));
          console.log('[PettyCash] loadTransactors - employeeOptions created:', employeeOptions.length);
          setTransactorOptions(employeeOptions);
          break;
        case 'su':
          // Handle both array and ensure it's an array
          const suppliersArray = Array.isArray(suppliers) ? suppliers : [];
          const supplierOptions = suppliersArray.map(sup => ({
            value: sup.supplier_Id || sup.Supplier_Id || sup.supplierId || sup.SupplierId,
            label: sup.supplier_NameEn || sup.Supplier_NameEn || sup.supplierNameEn || sup.SupplierNameEn || sup.supplier_Name || sup.Supplier_Name || '',
          }));
          setTransactorOptions(supplierOptions);
          break;
        case 'ot':
          // Handle both array and ensure it's an array
          const otherPayeesArray = Array.isArray(otherPayees) ? otherPayees : [];
          const otherPayeeOptions = otherPayeesArray.map(op => ({
            value: op.otherPayee_Id || op.OtherPayee_Id || op.otherPayeeId || op.OtherPayeeId,
            label: op.otherPayee_Name || op.OtherPayee_Name || op.otherPayeeName || op.OtherPayeeName || '',
          }));
          setTransactorOptions(otherPayeeOptions);
          break;
        default:
          setTransactorOptions([]);
      }
    } catch (error) {
      console.error("Error loading transactors:", error);
      Swal.fire("Error", "Failed to load transactor data", "error");
    } finally {
      setLoadingTransactors(false);
    }
  };

  const loadBankAccounts = async (bankId) => {
    try {
      setLoadingTransactors(true);
      const accountsResponse = await getBankAccounts({ bankId: bankId });
      // Extract bankAccounts array from response object (response has {bankAccounts: [...], totalCount, page, pageSize})
      const accountsArray = accountsResponse?.bankAccounts || accountsResponse || [];
      const accountOptions = accountsArray.map(acc => {
        const accountHolderName = acc.accountHolderName || acc.AccountHolderName || '';
        const accountNumber = acc.accountNumber || acc.AccountNumber || '';
        const label = accountHolderName && accountNumber
          ? `${accountHolderName} - ${accountNumber}`
          : accountHolderName || accountNumber || acc.bankAccount_Name || acc.BankAccount_Name || '';

        return {
          value: acc.bankAccountId || acc.BankAccountId || acc.bankAccount_Id || acc.BankAccount_Id,
          label: label,
        };
      });
      setBankAccounts(accountOptions);
      setTransactorOptions(accountOptions);
    } catch (error) {
      console.error("Error loading bank accounts:", error);
      Swal.fire("Error", "Failed to load bank accounts", "error");
    } finally {
      setLoadingTransactors(false);
    }
  };

  const handleFormChange = (field, value) => {
    if (field === 'TransactorType') {
      setForm(prev => ({
        ...prev,
        [field]: value,
        TransactorId: null
      }));
      setSelectedBank(null);
    } else if (field === 'TransactorId') {
      setForm(prev => ({ ...prev, [field]: value }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleBusinessChange = (option) => {
    console.log('[PettyCash] handleBusinessChange called with:', option);
    setSelectedBusiness(option);
    setSelectedBranch(null);
    setBranches([]);
    // Reload employees with new business filter
    loadEmployees();
  };

  const handleBankChange = (option) => {
    setSelectedBank(option);
    if (option) {
      loadBankAccounts(option.value);
    } else {
      setBankAccounts([]);
      setTransactorOptions([]);
      setForm(prev => ({ ...prev, TransactorId: null }));
    }
  };

  const handleSave = async () => {
    // Prevent double-click / repeated save
    if (saveLoading) return;
    setSaveLoading(true);
    try {
      // Get BusinessId from selected business or session
      const businessId = selectedBusiness?.value || session.businessId;
      const branchId = selectedBranch?.value || session.branchId;

      // MANDATORY: BusinessId is required for cash ledger operations
      if (!businessId) {
        Swal.fire({
          icon: 'error',
          title: 'Company Required',
          text: 'Please select a company. Company is mandatory for creating petty cash entries.',
        });
        return;
      }

      // Validation
      if (!form.Date) {
        Swal.fire("Error", "Please select a date", "error");
        return;
      }
      if (!form.Amount || form.Amount <= 0) {
        Swal.fire("Error", "Please enter a valid amount", "error");
        return;
      }
      if (!form.TransactorType) {
        Swal.fire("Error", "Please select a transactor type", "error");
        return;
      }
      if (!form.TransactorId) {
        Swal.fire("Error", "Please select a transactor", "error");
        return;
      }

      // Check day closing before allowing save (use stricter validation)
      const selectedDateOnly = new Date(form.Date);
      selectedDateOnly.setHours(0, 0, 0, 0);

      const checkRequest = {
        transactionDate: formatDateForAPI(selectedDateOnly, false),
        businessId: businessId,
        branchId: branchId,
        checkPreviousDays: true  // Enable stricter validation
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);

      // Check if selected date itself is closed
      if (dayClosingCheck.isClosed) {
        const message = dayClosingCheck.isHoliday
          ? `Holiday has been marked for ${formatDateForDisplay(selectedDateOnly, 'date')}. Please select a different date.`
          : `Day closing has been marked for ${formatDateForDisplay(selectedDateOnly, 'date')}. Please select a different date.`;
        Swal.fire("Error", message, "error");
        return;
      }

      // Check if any previous days are not closed (stricter validation)
      if (dayClosingCheck.firstUnclosedDate) {
        const unclosedDateStr = formatDateForDisplay(new Date(dayClosingCheck.firstUnclosedDate), 'date');
        Swal.fire("Error", `Day closing is not done for ${unclosedDateStr}. Please close that day first before creating transactions for ${formatDateForDisplay(selectedDateOnly, 'date')}.`, "error");
        return;
      }

      const cashLedgerData = {
        SlNo: 0, // Will be auto-calculated in stored procedure
        Date: formatDateForAPI(form.Date, true),
        Debit: isReceipt ? form.Amount : null,
        Credit: !isReceipt ? form.Amount : null,
        Notes: form.Notes || null,
        TransactorType: form.TransactorType,
        TransactorId: form.TransactorId,
        CostHeadId: form.CostHeadId || null,
        BusinessId: businessId,
        BranchId: branchId,
        CreatedBy: session.userId,
      };

      const result = await saveCashLedger(cashLedgerData);

      if (result.isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: result.message || 'Cash ledger entry saved successfully',
        });

        // Reset form (but keep business/branch if from session)
        setForm({
          ...defaultForm,
          Date: new Date(),
        });
        setSelectedBank(null);
        setBankAccounts([]);
        setTransactorOptions([]);
        // Don't reset business/branch if they're from session (auto-selected)
        if (!session.businessId) {
          setSelectedBusiness(null);
        }
        if (!session.branchId) {
          setSelectedBranch(null);
        }
      } else {
        Swal.fire("Error", result.message || "Failed to save cash ledger entry", "error");
      }
    } catch (error) {
      console.error("Error saving cash ledger:", error);
      Swal.fire("Error", "An error occurred while saving the cash ledger entry", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title={breadcrumbTitle} breadcrumbItem={breadcrumbTitle} />

          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">{pageTitle}</h4>

                  {isViewOnly && (
                    <div className="alert alert-info">
                      <i className="mdi mdi-information me-2"></i>
                      You have view-only access. You cannot make changes.
                    </div>
                  )}

                  {hasEditOrHigher && (
                    <Form>
                      <Row>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Company <span className="text-danger">*</span></Label>
                            <Select
                              value={selectedBusiness}
                              onChange={handleBusinessChange}
                              options={businesses.map(b => ({
                                value: b.business_Id || b.businessId,
                                label: b.business_NameEn || b.business_Name || b.businessNameEn || ''
                              }))}
                              isClearable={!session.businessId}
                              isDisabled={isViewOnly || isReadOnly || loadingBusinesses || !!session.businessId}
                              placeholder="Select company"
                              styles={selectStyles}
                              isLoading={loadingBusinesses}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Branch</Label>
                            <Select
                              value={selectedBranch}
                              onChange={(option) => {
                                setSelectedBranch(option);
                                // Reload employees when branch changes
                                loadEmployees();
                              }}
                              options={branches.map(b => ({
                                value: b.branch_Id || b.branchId,
                                label: `${b.branch_NameEn || b.branch_Name || b.branchNameEn || ''} - ${b.branch_NameAr || b.branchNameAr || ''}`.trim()
                              }))}
                              isClearable={!session.branchId}
                              isDisabled={isViewOnly || isReadOnly || loadingBranches || !selectedBusiness || !!session.branchId}
                              placeholder={selectedBusiness ? "Select branch" : "Select company first"}
                              styles={selectStyles}
                              isLoading={loadingBranches}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Date <span className="text-danger">*</span></Label>
                            <DatePicker
                              selected={form.Date}
                              onChange={(date) => handleFormChange("Date", date)}
                              dateFormat="dd/MM/yyyy"
                              className="form-control"
                              maxDate={new Date()}
                              disabled={isViewOnly}
                            // Keep date picker enabled even when form is disabled (so user can change date after selecting closed day)
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Amount <span className="text-danger">*</span></Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={form.Amount}
                              onChange={(e) => handleFormChange("Amount", parseFloat(e.target.value) || 0)}
                              disabled={isViewOnly || isReadOnly}
                              placeholder="Enter amount"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Transactor Type <span className="text-danger">*</span></Label>
                            <Select
                              value={transactorTypeOptions.find(opt => opt.value === form.TransactorType) || null}
                              onChange={(option) => handleFormChange("TransactorType", option?.value || null)}
                              options={transactorTypeOptions}
                              isClearable
                              isDisabled={isViewOnly || isReadOnly}
                              placeholder="Select transactor type"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Transactor Selection <span className="text-danger">*</span></Label>
                            {form.TransactorType === 'ba' && !selectedBank ? (
                              <Select
                                value={transactorOptions.find(opt => opt.value === form.TransactorId) || null}
                                onChange={handleBankChange}
                                options={transactorOptions}
                                isClearable
                                isDisabled={isViewOnly || isReadOnly || loadingTransactors}
                                placeholder="Select bank"
                                isLoading={loadingTransactors}
                                styles={selectStyles}
                              />
                            ) : (
                              <Select
                                value={transactorOptions.find(opt => opt.value === form.TransactorId) || null}
                                onChange={(option) => handleFormChange("TransactorId", option?.value || null)}
                                options={transactorOptions}
                                isClearable
                                isDisabled={isViewOnly || isReadOnly || loadingTransactors || (form.TransactorType === 'ba' && !selectedBank)}
                                placeholder={
                                  form.TransactorType === 'ba' && !selectedBank
                                    ? "Select bank first"
                                    : form.TransactorType === 'ba' && selectedBank
                                      ? "Select bank account"
                                      : "Select transactor"
                                }
                                isLoading={loadingTransactors}
                                styles={selectStyles}
                              />
                            )}
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Cost Head</Label>
                            <Select
                              value={costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === form.CostHeadId)
                                ? {
                                  value: form.CostHeadId,
                                  label: costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === form.CostHeadId)?.costHead_Name ||
                                    costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === form.CostHeadId)?.CostHead_Name || ''
                                }
                                : null}
                              onChange={(option) => handleFormChange("CostHeadId", option?.value || null)}
                              options={costHeads.map(ch => ({
                                value: ch.costHead_Id || ch.CostHead_Id,
                                label: ch.costHead_Name || ch.CostHead_Name || '',
                              }))}
                              isClearable
                              isDisabled={isViewOnly || isReadOnly}
                              placeholder="Select cost head"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Notes</Label>
                            <Input
                              type="textarea"
                              rows="3"
                              value={form.Notes}
                              onChange={(e) => handleFormChange("Notes", e.target.value)}
                              disabled={isViewOnly || isReadOnly}
                              placeholder="Enter notes"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <div className="d-flex gap-2">
                            <Button
                              color="primary"
                              onClick={handleSave}
                              disabled={isViewOnly || isReadOnly || saveLoading}
                            >
                              {saveLoading ? (
                                <>
                                  <ClipLoader size={15} color="#fff" className="me-2" />
                                  Saving...
                                </>
                              ) : (
                                "Save"
                              )}
                            </Button>
                            <Button
                              color="secondary"
                              onClick={() => {
                                setForm({
                                  ...defaultForm,
                                  Date: new Date(),
                                });
                                setSelectedBank(null);
                                setBankAccounts([]);
                                setTransactorOptions([]);
                              }}
                              disabled={isViewOnly || isReadOnly || saveLoading}
                            >
                              Reset
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}

                  {!hasEditOrHigher && (
                    <div className="alert alert-warning">
                      <i className="mdi mdi-alert me-2"></i>
                      You do not have permission to add or edit entries.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

